<template>
  <div class="importar-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="importar-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-header-left">
          <div class="page-icon-box">
            <v-icon color="#F2F2ED" size="20">mdi-file-upload-outline</v-icon>
          </div>
          <div>
            <h1 class="page-title">Importar miembros</h1>
            <p class="page-sub">Da de alta varios miembros a la vez desde un archivo Excel</p>
          </div>
        </div>
        <button class="btn-ghost" @click="$router.push('/miembros')">
          <v-icon size="15" class="mr-2">mdi-arrow-left</v-icon>
          Volver
        </button>
      </header>

      <!-- Paso 1: Subir archivo -->
      <section v-if="paso === 1" class="fade-in delay-100">
        <div class="card-flat">
          <div class="card-flat-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Paso 1 de 3 — Selecciona tu archivo
            </span>
          </div>

          <div class="card-flat-body">
            <div class="info-box mb-lg">
              <v-icon size="16" color="#8C8C93" class="mr-2 mt-1">mdi-information-outline</v-icon>
              <div>
                Solo se importa el <strong>nombre</strong> de cada miembro. La matrícula se genera
                automáticamente y el resto de los datos (teléfono, dirección, etc.) los puedes completar
                después editando cada miembro.
              </div>
            </div>

            <div class="dropzone" :class="{ 'dropzone-active': arrastrando }" @dragover.prevent="arrastrando = true"
              @dragleave.prevent="arrastrando = false" @drop.prevent="onDrop" @click="$refs.fileInput.click()">
              <v-icon size="36" color="#5C5C62" class="mb-md">mdi-file-excel-outline</v-icon>
              <div class="dropzone-text">Arrastra tu archivo aquí o haz clic para buscarlo</div>
              <div class="dropzone-hint">Formatos aceptados: .xlsx, .xls, .csv</div>
              <input ref="fileInput" type="file" accept=".xlsx,.xls,.csv" class="hidden-input" @change="onFileChange" />
            </div>

            <div class="template-row mt-lg">
              <span class="template-text">¿No tienes un archivo listo?</span>
              <button class="link-btn" @click="descargarPlantilla">
                <v-icon size="14" class="mr-1">mdi-download</v-icon>
                Descargar plantilla de ejemplo
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Paso 2: Vista previa -->
      <section v-if="paso === 2" class="fade-in delay-100">
        <div class="card-flat">
          <div class="card-flat-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Paso 2 de 3 — Confirma la columna y revisa la vista previa
            </span>
          </div>

          <div class="card-flat-body">
            <div class="field-row">
              <div class="field">
                <label class="field-label">Mi archivo tiene fila de encabezado</label>
                <div class="switch-row">
                  <label class="switch">
                    <input type="checkbox" v-model="tieneEncabezado" @change="reprocesarArchivo" />
                    <span class="switch-slider"></span>
                  </label>
                  <span class="switch-text">{{ tieneEncabezado ? 'Sí, la primera fila son títulos' : 'No, todas las filas son datos' }}</span>
                </div>
              </div>

              <div class="field" v-if="tieneEncabezado && encabezados.length > 1">
                <label class="field-label">Columna que contiene el nombre</label>
                <select v-model="columnaSeleccionada" class="select-input" @change="reprocesarArchivo">
                  <option v-for="(col, index) in encabezados" :key="index" :value="index">{{ col || `Columna ${index + 1}` }}</option>
                </select>
              </div>
            </div>

            <div class="section-divider"></div>

            <div class="preview-summary">
              <span class="status-tag status-tag-success">{{ nombresValidos.length }} nombres listos</span>
              <span v-if="filasVacias > 0" class="status-tag status-tag-warning">{{ filasVacias }} filas vacías (se omiten)</span>
              <span class="preview-total">de {{ filasCrudo.length }} filas totales en el archivo</span>
            </div>

            <div class="preview-table-wrapper mt-md">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nombre</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(fila, index) in filasCrudo.slice(0, 50)" :key="index" :class="{ 'row-empty': !fila.trim() }">
                    <td class="cell-mono">{{ index + 1 }}</td>
                    <td class="cell-strong">{{ fila.trim() || '(vacío)' }}</td>
                    <td>
                      <v-icon v-if="fila.trim()" size="14" color="#C8FF4D">mdi-check</v-icon>
                      <v-icon v-else size="14" color="#FFB84D">mdi-alert</v-icon>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="filasCrudo.length > 50" class="preview-more">
                y {{ filasCrudo.length - 50 }} más…
              </div>
            </div>
          </div>

          <div class="card-flat-footer">
            <button class="btn-ghost" @click="reiniciar">
              <v-icon size="15" class="mr-2">mdi-arrow-left</v-icon>
              Elegir otro archivo
            </button>
            <button class="btn-primary" :disabled="nombresValidos.length === 0" @click="paso = 3">
              Continuar con {{ nombresValidos.length }} miembros
              <v-icon size="15" class="ml-2">mdi-arrow-right</v-icon>
            </button>
          </div>
        </div>
      </section>

      <!-- Paso 3: Confirmar -->
      <section v-if="paso === 3" class="fade-in delay-100">
        <div class="card-flat">
          <div class="card-flat-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Paso 3 de 3 — Confirmar importación
            </span>
          </div>

          <div class="card-flat-body">
            <div class="confirm-center">
              <div class="confirm-icon">
                <v-icon size="28" color="#0E0E10">mdi-account-multiple-plus</v-icon>
              </div>
              <div class="confirm-value">{{ nombresValidos.length }}</div>
              <div class="confirm-label">miembros se registrarán en el sistema</div>
            </div>

            <div class="alert-box mt-lg">
              <v-icon size="18" color="#FFB84D">mdi-alert</v-icon>
              <div class="alert-text">
                Esta acción no se puede deshacer masivamente — tendrías que eliminar los miembros uno por
                uno si te equivocas. Revisa la vista previa antes de continuar.
              </div>
            </div>
          </div>

          <div class="card-flat-footer">
            <button class="btn-ghost" @click="paso = 2" :disabled="importando">
              <v-icon size="15" class="mr-2">mdi-arrow-left</v-icon>
              Regresar a revisar
            </button>
            <button class="btn-primary" @click="confirmarImportacion" :disabled="importando">
              <span v-if="!importando">
                <v-icon size="15" class="mr-2">mdi-check</v-icon>
                Importar {{ nombresValidos.length }} miembros
              </span>
              <span v-else class="loading-inline"><span class="spinner-sm"></span>Importando…</span>
            </button>
          </div>
        </div>
      </section>

      <!-- Resultado -->
      <section v-if="paso === 4" class="fade-in delay-100">
        <div class="card-flat">
          <div class="card-flat-body result-body">
            <div class="result-icon">
              <v-icon size="32" color="#0E0E10">mdi-check</v-icon>
            </div>
            <h2 class="result-title">Importación completada</h2>

            <div class="result-stats">
              <div class="result-stat">
                <div class="result-stat-value">{{ resultado.insertados }}</div>
                <div class="result-stat-label">Importados</div>
              </div>
              <div class="result-stat" v-if="resultado.fallidos && resultado.fallidos.length > 0">
                <div class="result-stat-value result-stat-error">{{ resultado.fallidos.length }}</div>
                <div class="result-stat-label">Con error</div>
              </div>
            </div>

            <div v-if="resultado.fallidos && resultado.fallidos.length > 0" class="fallidos-list mt-lg">
              <span class="mini-eyebrow">Filas con problemas</span>
              <div v-for="(f, index) in resultado.fallidos" :key="index" class="fallido-item">
                Fila {{ f.fila }}: {{ f.motivo }}
              </div>
            </div>
          </div>

          <div class="card-flat-footer card-flat-footer-center">
            <button class="btn-primary" @click="$router.push('/miembros')">
              <v-icon size="15" class="mr-2">mdi-account-group</v-icon>
              Ver lista de miembros
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="mostrarMensaje" :timeout="4000" :color="mensaje.color" top right>
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <span>{{ mensaje.texto }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import HttpService from '../../Servicios/HttpService';

export default {
  name: "ImportarMiembros",

  data: () => ({
    paso: 1,
    arrastrando: false,
    archivo: null,
    workbook: null,
    tieneEncabezado: true,
    encabezados: [],
    columnaSeleccionada: 0,
    filasCrudo: [],
    importando: false,
    resultado: { insertados: 0, total: 0, fallidos: [] },
    mostrarMensaje: false,
    mensaje: { texto: '', color: '' }
  }),

  computed: {
    mensajeIcon() {
      switch (this.mensaje.color) {
        case 'success': return 'mdi-check-circle';
        case 'error': return 'mdi-alert-circle';
        case 'warning': return 'mdi-alert';
        default: return 'mdi-information';
      }
    },

    nombresValidos() {
      return this.filasCrudo.map(f => f.trim()).filter(f => f.length > 0);
    },

    filasVacias() {
      return this.filasCrudo.filter(f => !f.trim()).length;
    }
  },

  methods: {
    onDrop(e) {
      this.arrastrando = false;
      const archivo = e.dataTransfer.files[0];
      if (archivo) this.procesarArchivo(archivo);
    },

    onFileChange(e) {
      const archivo = e.target.files[0];
      if (archivo) this.procesarArchivo(archivo);
    },

    procesarArchivo(archivo) {
      this.archivo = archivo;
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          this.workbook = XLSX.read(data, { type: 'array' });
          this.detectarColumnaNombre();
          this.reprocesarArchivo();
          this.paso = 2;
        } catch (error) {
          console.error('Error leyendo el archivo:', error);
          this.mostrarError('No se pudo leer el archivo. ¿Es un Excel o CSV válido?');
        }
      };

      reader.readAsArrayBuffer(archivo);
    },

    obtenerFilasRaw() {
      const primeraHoja = this.workbook.SheetNames[0];
      const hoja = this.workbook.Sheets[primeraHoja];
      return XLSX.utils.sheet_to_json(hoja, { header: 1, defval: '' });
    },

    detectarColumnaNombre() {
      const filas = this.obtenerFilasRaw();
      if (filas.length === 0) return;

      const primeraFila = filas[0].map(c => String(c));
      // ¿La primera fila parece encabezado? (alguna celda dice "nombre")
      const indiceNombre = primeraFila.findIndex(c => /nombre/i.test(c));

      this.encabezados = primeraFila;
      this.columnaSeleccionada = indiceNombre >= 0 ? indiceNombre : 0;
      this.tieneEncabezado = indiceNombre >= 0 || primeraFila.length > 0;
    },

    reprocesarArchivo() {
      if (!this.workbook) return;

      const filas = this.obtenerFilasRaw();
      const desde = this.tieneEncabezado ? 1 : 0;

      this.filasCrudo = filas
        .slice(desde)
        .map(fila => (fila[this.columnaSeleccionada] !== undefined ? String(fila[this.columnaSeleccionada]) : ''));
    },

    reiniciar() {
      this.paso = 1;
      this.archivo = null;
      this.workbook = null;
      this.filasCrudo = [];
      this.encabezados = [];
      this.$refs.fileInput.value = '';
    },

    descargarPlantilla() {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([
        ['Nombre'],
        ['Ej. Juan Pérez López'],
      ]);
      ws['!cols'] = [{ wch: 30 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Miembros');
      XLSX.writeFile(wb, 'plantilla_importar_miembros.xlsx');
    },

    async confirmarImportacion() {
      this.importando = true;

      const payload = {
        metodo: 'importar',
        miembros: this.nombresValidos.map(nombre => ({ nombre }))
      };

      try {
        const respuesta = await HttpService.registrar(payload, 'miembros.php');

        if (respuesta && respuesta.success) {
          this.resultado = respuesta;
          this.paso = 4;
        } else {
          this.mostrarError('No se pudo completar la importación');
        }
      } catch (error) {
        console.error('Error importando miembros:', error);
        this.mostrarError('Error al importar los miembros');
      } finally {
        this.importando = false;
      }
    },

    mostrarError(texto) {
      this.mostrarMensaje = true;
      this.mensaje = { texto, color: 'error' };
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.importar-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #FFB84D;
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.importar-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.importar-shell {
  position: relative;
  z-index: 1;
  max-width: 760px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 20px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

/* ===== CARD ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.card-flat-head {
  padding: 18px 24px;
  border-bottom: 1px solid var(--card-border);
}

.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.tick-mark {
  width: 12px;
  height: 1px;
  background: var(--accent);
}

.card-flat-body {
  padding: 24px;
}

.card-flat-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
  flex-wrap: wrap;
}

.card-flat-footer-center {
  justify-content: center;
}

/* ===== INFO BOX ===== */
.info-box {
  display: flex;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 14px 16px;
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.6;
}

.info-box strong {
  color: var(--text-primary);
}

/* ===== DROPZONE ===== */
.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 1.5px dashed var(--card-border);
  border-radius: var(--radius);
  padding: 48px 20px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.dropzone:hover,
.dropzone-active {
  border-color: var(--accent);
  background: var(--accent-dim);
}

.dropzone-text {
  font-size: 13.5px;
  color: var(--text-primary);
  font-weight: 500;
}

.dropzone-hint {
  font-size: 11.5px;
  color: var(--text-tertiary);
  margin-top: 6px;
}

.hidden-input {
  display: none;
}

.template-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.template-text {
  font-size: 12.5px;
  color: var(--text-tertiary);
}

.link-btn {
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  color: var(--accent);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
}

.link-btn:hover {
  text-decoration: underline;
}

/* ===== FIELDS ===== */
.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.select-input {
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 12px;
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: 13.5px;
  outline: none;
}

.select-input:focus {
  border-color: var(--accent);
}

.switch-row {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 42px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: var(--card-border);
  border-radius: 20px;
  transition: background 0.15s ease;
}

.switch-slider::before {
  content: "";
  position: absolute;
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background: var(--text-secondary);
  border-radius: 50%;
  transition: transform 0.15s ease, background 0.15s ease;
}

.switch input:checked + .switch-slider {
  background: var(--accent-dim);
}

.switch input:checked + .switch-slider::before {
  transform: translateX(16px);
  background: var(--accent);
}

.switch-text {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.section-divider {
  height: 1px;
  background: var(--card-border);
  margin: 20px 0;
}

/* ===== PREVIEW ===== */
.preview-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: var(--radius);
  font-size: 11px;
  font-weight: 600;
  font-family: var(--font-mono);
}

.status-tag-success {
  background: var(--accent-dim);
  color: var(--accent);
}

.status-tag-warning {
  background: rgba(255, 184, 77, 0.12);
  color: var(--warning);
}

.preview-total {
  font-size: 12px;
  color: var(--text-tertiary);
}

.preview-table-wrapper {
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
  max-height: 340px;
  overflow-y: auto;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
}

.preview-table th {
  text-align: left;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  background: rgba(255, 255, 255, 0.02);
  padding: 8px 14px;
  border-bottom: 1px solid var(--card-border);
  position: sticky;
  top: 0;
}

.preview-table td {
  padding: 8px 14px;
  border-bottom: 1px solid var(--card-border);
}

.preview-table tr:last-child td {
  border-bottom: none;
}

.row-empty {
  background: rgba(255, 184, 77, 0.04);
}

.cell-mono {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-tertiary);
}

.cell-strong {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}

.preview-more {
  padding: 10px 14px;
  font-size: 11.5px;
  color: var(--text-tertiary);
  text-align: center;
}

/* ===== CONFIRM STEP ===== */
.confirm-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.confirm-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.confirm-value {
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.confirm-label {
  font-size: 13px;
  color: var(--text-secondary);
  margin-top: 8px;
}

.alert-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(255, 184, 77, 0.08);
  border: 1px solid rgba(255, 184, 77, 0.25);
  border-radius: var(--radius);
  padding: 12px 14px;
}

.alert-text {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
}

/* ===== RESULT STEP ===== */
.result-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.result-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.result-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 20px;
}

.result-stats {
  display: flex;
  gap: 32px;
}

.result-stat-value {
  font-family: var(--font-display);
  font-size: 28px;
  font-weight: 700;
  color: var(--accent);
}

.result-stat-error {
  color: var(--warning);
}

.result-stat-label {
  font-size: 11.5px;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 4px;
}

.fallidos-list {
  width: 100%;
  text-align: left;
}

.mini-eyebrow {
  display: block;
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  margin-bottom: 8px;
}

.fallido-item {
  font-size: 12px;
  color: var(--text-secondary);
  padding: 6px 10px;
  background: rgba(255, 184, 77, 0.06);
  border-radius: var(--radius);
  margin-bottom: 4px;
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 11px 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: default;
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 11px 18px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover:not(:disabled) {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.btn-ghost:disabled {
  opacity: 0.5;
  cursor: default;
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner-sm {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(14, 14, 16, 0.3);
  border-top-color: var(--bg);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in, .spinner-sm { animation: none; }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 600px) {
  .importar-page {
    padding: 20px;
  }

  .field-row {
    grid-template-columns: 1fr;
  }

  .card-flat-footer {
    flex-direction: column-reverse;
  }

  .card-flat-footer .btn-primary,
  .card-flat-footer .btn-ghost {
    width: 100%;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Miembros/ImportarMiembros.vue